import { Button } from "@devhop/ui";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CircleCheckIcon,
} from "lucide-react";
import type {
  EventRehearsalSummaryDTO,
  RehearsalSessionDTO,
} from "../../domain/contracts";

function formatMoney(value: number) {
  return `${value.toLocaleString()} ກີບ`;
}

type EventRehearsalReportSectionProps = {
  hasTeam: boolean;
  summary: EventRehearsalSummaryDTO | undefined;
  sessions: RehearsalSessionDTO[] | undefined;
  isLoading?: boolean;
  onOpenSchedule: () => void;
};

function StatCell({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={
        highlight
          ? "flex flex-col gap-0.5 bg-primary/5 px-4 py-3"
          : "flex flex-col gap-0.5 bg-card px-4 py-3"
      }
    >
      <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
        {highlight ? <CircleCheckIcon className="size-3 text-primary" /> : null}
        {label}
      </span>
      <span className="font-semibold text-base tabular-nums tracking-tight">
        {value}
      </span>
      <span className="text-muted-foreground text-xs">{hint}</span>
    </div>
  );
}

export function EventRehearsalReportSection({
  hasTeam,
  summary,
  sessions,
  isLoading,
  onOpenSchedule,
}: EventRehearsalReportSectionProps) {
  const planned = sessions?.filter((s) => s.status === "planned").length ?? 0;
  const completed =
    sessions?.filter((s) => s.status === "completed").length ?? 0;
  const totalSessions = sessions?.length ?? 0;
  const rehearsalDays = summary?.totalRehearsalDays ?? 0;
  const rehearsalAmount = summary?.totalRehearsalAmount ?? 0;

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border bg-primary/5">
            <CalendarDaysIcon className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
              ວັນຊ້ອມ
            </p>
            <h3 className="mt-0.5 font-semibold text-base">ສະຫຼຸບການຊ້ອມ</h3>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ສະຖານະ «ສຳເລັດ» ເທົ່ານັ້ນທີ່ນັບຄ່າຕົວໃນ payout
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!hasTeam}
          onClick={onOpenSchedule}
        >
          ເຂົ້າລາຍການວັນຊ້ອມ
          <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      {isLoading ? (
        <p className="px-5 py-4 text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      ) : !hasTeam ? (
        <p className="px-5 py-4 text-muted-foreground text-sm">
          ຍັງບໍ່ມີນັກສະແດງໃນທີມ
        </p>
      ) : totalSessions === 0 ? (
        <p className="px-5 py-4 text-muted-foreground text-sm">
          ຍັງບໍ່ມີລາຍການວັນຊ້ອມ
        </p>
      ) : (
        <div className="grid gap-px border-t bg-border sm:grid-cols-2 lg:grid-cols-4">
          <StatCell
            label="ລາຍການທັງໝົດ"
            value={String(totalSessions)}
            hint="ທຸກສະຖານະ"
          />
          <StatCell
            label="ວາງແຜນ"
            value={String(planned)}
            hint="ຍັງບໍ່ນັບ payout"
          />
          <StatCell
            label="ສຳເລັດ"
            value={String(completed)}
            hint="ນັບຄ່າຕົວ"
            highlight
          />
          <StatCell
            label="ຄາດຄ່າຈ້າງຊ້ອມ"
            value={rehearsalDays > 0 ? formatMoney(rehearsalAmount) : "—"}
            hint={
              rehearsalDays > 0
                ? `${rehearsalDays} ວັນຊ້ອມ (completed)`
                : "ຍັງບໍ່ມີວັນທີ່ຢືນຢັນ"
            }
            highlight={rehearsalDays > 0}
          />
        </div>
      )}
    </section>
  );
}
